/**
 * Mega menu: promo distribution, video playback, panel state.
 * Clones promo cards from .mega-menu__promo-staging to desktop/mobile targets on init and panel open.
 * Preloads video on menu hover; plays when visible via IntersectionObserver.
 */
(function () {
  const PANEL_TRIGGER = '[data-mega-trigger][aria-controls]';
  const VIDEO_SELECTOR = '.mega-menu__promo-video';
  const VIDEO_THRESHOLD = 0.3;

  const MegaMenu = {
    initialized: false,
    panelObservers: [],
    videoObserver: null,
    videoVisibility: new WeakMap(),
    syncRaf: null,

    init() {
      this._distributePromos();
      this._disconnectPanelObservers();
      this._bindPanelState();
      this._observeVideos();

      if (this.initialized) return;

      this.initialized = true;

      document.addEventListener('visibilitychange', () => this._scheduleVideoSync());
      document.addEventListener('header-drawer:open', () => {
        this._distributePromos();
        this._observeVideos();
        this._scheduleVideoSync();
      });
      document.addEventListener('header-drawer:close', () => this._scheduleVideoSync());
      // Re-distribute when a drawer submenu opens (deferred hydration / late targets)
      document.addEventListener(
        'toggle',
        (event) => {
          const details = event.target;
          if (!(details instanceof HTMLDetailsElement) || !details.open) return;
          if (!details.closest('header-drawer')) return;
          this._distributePromos();
          this._observeVideos();
          this._scheduleVideoSync();
        },
        true
      );
      window.addEventListener('resize', () => this._scheduleVideoSync(), { passive: true });

      document.addEventListener('shopify:section:load', () => this.init());
      document.addEventListener('shopify:section:reorder', () => this.init());
    },

    _bindPanelState() {
      const triggers = document.querySelectorAll(PANEL_TRIGGER);

      triggers.forEach((trigger) => {
        if (!(trigger instanceof HTMLElement)) return;

        const controls = trigger.getAttribute('aria-controls');
        if (!controls) return;

        const panel = document.getElementById(controls);
        if (!(panel instanceof HTMLElement) || !panel.hasAttribute('data-mega-menu-panel')) return;

        const menuKey = panel.querySelector('[data-promo-target]')?.getAttribute('data-promo-target');
        if (menuKey) {
          trigger.addEventListener('pointerenter', () => this._preloadPromoMedia(menuKey), {
            passive: true,
            once: true,
          });
        }

        const syncState = () => {
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
          panel.classList.toggle('is-open', isExpanded);
          panel.classList.toggle('is-closed', !isExpanded);
          if (isExpanded) {
            this._distributePromos();
            this._observeVideos();
          }
          this._scheduleVideoSync();
        };

        syncState();

        const observer = new MutationObserver(syncState);
        observer.observe(trigger, {
          attributes: true,
          attributeFilter: ['aria-expanded'],
        });

        this.panelObservers.push(observer);
      });
    },

    _distributePromos() {
      const staging = document.querySelector('.mega-menu__promo-staging');
      if (!staging) return;

      const sources = staging.querySelectorAll('.mega-menu__promo-source[data-promo-for]');
      const targets = document.querySelectorAll('[data-promo-target]');

      const targetMap = new Map();
      for (const t of targets) {
        t.replaceChildren();
        let next = t.nextElementSibling;
        while (next?.classList.contains('mega-menu__promo-column') && !next.hasAttribute('data-promo-target')) {
          const toRemove = next;
          next = next.nextElementSibling;
          toRemove.remove();
        }
        const key = t.getAttribute('data-promo-target');
        if (key) {
          const list = targetMap.get(key) ?? [];
          list.push(t);
          targetMap.set(key, list);
        }
      }

      sources.forEach((source) => {
        const menuKey = source.getAttribute('data-promo-for');
        const targetList = targetMap.get(menuKey);
        if (!targetList?.length) return;

        const card = source.querySelector('.mega-menu__promo-card');
        if (!card) return;

        targetList.forEach((target) => {
          const clone = card.cloneNode(true);
          const isMobileStrip = target.classList.contains('mobile-nav__media-strip');
          clone.querySelectorAll('img').forEach((img) => img.removeAttribute('loading'));
          const video = clone.querySelector('.mega-menu__promo-video');
          if (video instanceof HTMLVideoElement) video.load();
          if (isMobileStrip) {
            clone.classList.add('mobile-nav__media-item');
            clone.querySelector('.mega-menu__promo-image')?.classList.add('mobile-nav__media-image');
            clone.querySelector('.mega-menu__promo-video')?.classList.add('mobile-nav__media-video');
            clone.querySelector('.mega-menu__promo-caption')?.classList.add('mobile-nav__media-caption');
            target.appendChild(clone);
          } else {
            if (target.children.length === 0) {
              target.appendChild(clone);
            } else {
              const column = document.createElement('div');
              column.className = 'mega-menu__promo-column';
              column.appendChild(clone);
              target.parentNode.insertBefore(column, target.nextSibling);
            }
          }
          const layout = target.closest('.mega-menu__layout');
          if (layout) layout.classList.remove('mega-menu__layout--no-promo');
        });
      });

      targets.forEach((t) => {
        if (!t.children.length) {
          // Keep empty mobile strips — drawer may hydrate/open after first distribute
          if (t.classList.contains('mobile-nav__media-strip')) return;

          t.remove();
          const wrapper = t.closest('.mobile-nav__media-strip-wrapper');
          if (wrapper && !wrapper.children.length) wrapper.remove();
        }
      });
    },

    _preloadPromoMedia(menuKey) {
      const staging = document.querySelector('.mega-menu__promo-staging');
      if (!staging) return;
      staging
        .querySelectorAll(`.mega-menu__promo-source[data-promo-for="${menuKey}"] .mega-menu__promo-video`)
        .forEach((video) => {
          if (video instanceof HTMLVideoElement) video.load();
        });
    },

    _disconnectPanelObservers() {
      this.panelObservers.forEach((observer) => observer.disconnect());
      this.panelObservers = [];
    },

    _observeVideos() {
      const videos = Array.from(document.querySelectorAll(VIDEO_SELECTOR)).filter(
        (video) => video instanceof HTMLVideoElement
      );

      if (this.videoObserver) {
        this.videoObserver.disconnect();
      }

      this.videoObserver = null;
      this.videoVisibility = new WeakMap();

      if (!videos.length || !('IntersectionObserver' in window)) return;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (!(video instanceof HTMLVideoElement)) return;
            const inView = entry.isIntersecting && entry.intersectionRatio >= VIDEO_THRESHOLD;
            this.videoVisibility.set(video, inView);
            this._syncVideo(video);
          });
        },
        { threshold: [0, VIDEO_THRESHOLD, 1] }
      );

      videos.forEach((video) => {
        this.videoVisibility.set(video, false);
        this.videoObserver?.observe(video);
      });

      this._scheduleVideoSync();
    },

    _scheduleVideoSync() {
      if (this.syncRaf) cancelAnimationFrame(this.syncRaf);
      this.syncRaf = requestAnimationFrame(() => {
        this.syncRaf = null;
        this._syncAllVideos();
      });
    },

    _syncAllVideos() {
      const videos = document.querySelectorAll(VIDEO_SELECTOR);
      videos.forEach((video) => {
        if (video instanceof HTMLVideoElement) {
          this._syncVideo(video);
        }
      });
    },

    _syncVideo(video) {
      const shouldPlay =
        this.videoVisibility.get(video) === true &&
        this._isActuallyVisible(video) &&
        document.visibilityState === 'visible';

      video.muted = true;
      video.playsInline = true;

      if (shouldPlay) {
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(() => {});
        }
      } else {
        video.pause();
      }
    },

    _isActuallyVisible(element) {
      if (!(element instanceof HTMLElement)) return false;

      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (Number.parseFloat(style.opacity || '1') === 0) return false;

      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    },
  };

  window.MegaMenu = MegaMenu;

  document.addEventListener('DOMContentLoaded', () => {
    MegaMenu.init();
  });
})();
